// SectorHeatmap.js — NSE sector performance
// Direct Yahoo Finance fetch (no fetchQuote dependency)
import React, { useState, useEffect, useCallback } from "react";
import "./SectorHeatmap.css";

const SECTORS = [
  { name: "Nifty IT", ticker: "^CNXIT" },
  { name: "Nifty Bank", ticker: "^NSEBANK" },
  { name: "Nifty Pharma", ticker: "^CNXPHARMA" },
  { name: "Nifty Auto", ticker: "^CNXAUTO" },
  { name: "Nifty FMCG", ticker: "^CNXFMCG" },
  { name: "Nifty Metal", ticker: "^CNXMETAL" },
  { name: "Nifty Realty", ticker: "^CNXREALTY" },
  { name: "Nifty PSU Bank", ticker: "^CNXPSUBANK" },
];

// Cache configuration
const CACHE_TTL = 3 * 60 * 1000; // 3 minutes
const _sectorCache = new Map();

function getSectorFromCache(ticker) {
  const cached = _sectorCache.get(ticker);
  if (!cached || Date.now() - cached.ts > CACHE_TTL) {
    _sectorCache.delete(ticker);
    return null;
  }
  return cached.data;
}

function cacheSector(ticker, data) {
  _sectorCache.set(ticker, { data, ts: Date.now() });
}

// Direct Yahoo Finance fetch for sector indices
async function fetchSectorData(ticker) {
  try {
    const cached = getSectorFromCache(ticker);
    if (cached) return cached;

    const url = `/yf/v8/finance/chart/${encodeURIComponent(
      ticker,
    )}?range=1d&interval=1d&includePrePost=false`;

    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const data = await res.json();
    const result = data?.chart?.result?.[0];

    if (!result) throw new Error("No chart data");

    const meta = result.meta;
    const close = meta.regularMarketPrice || 0;
    const prev = meta.chartPreviousClose || meta.previousClose || close;
    const change = +(close - prev).toFixed(2);

    const sectorData = {
      symbol: ticker,
      ticker: ticker,
      name: meta.longName || meta.shortName || ticker,
      price: close,
      previous_close: prev,
      change,
      change_percent: prev ? +((change / prev) * 100).toFixed(2) : 0,
      open: meta.regularMarketOpen || 0,
      high: meta.regularMarketDayHigh || 0,
      low: meta.regularMarketDayLow || 0,
      volume: meta.regularMarketVolume || 0,
      year_high: meta.fiftyTwoWeekHigh || 0,
      year_low: meta.fiftyTwoWeekLow || 0,
    };

    cacheSector(ticker, sectorData);
    return sectorData;
  } catch (e) {
    console.warn(`[Sector] Error fetching ${ticker}:`, e.message);
    throw e;
  }
}

function getHeatColors(pct) {
  const p = parseFloat(pct) || 0;
  if (p > 2)
    return {
      bg: "rgba(34,197,94,0.18)",
      border: "rgba(34,197,94,0.45)",
      text: "#4ade80",
      bar: "#22c55e",
    };
  if (p > 0.5)
    return {
      bg: "rgba(34,197,94,0.10)",
      border: "rgba(34,197,94,0.22)",
      text: "#22c55e",
      bar: "#22c55e",
    };
  if (p > 0)
    return {
      bg: "rgba(34,197,94,0.05)",
      border: "rgba(34,197,94,0.12)",
      text: "#16a34a",
      bar: "#16a34a",
    };
  if (p > -0.5)
    return {
      bg: "rgba(239,68,68,0.05)",
      border: "rgba(239,68,68,0.12)",
      text: "#dc2626",
      bar: "#ef4444",
    };
  if (p > -2)
    return {
      bg: "rgba(239,68,68,0.10)",
      border: "rgba(239,68,68,0.22)",
      text: "#ef4444",
      bar: "#ef4444",
    };
  return {
    bg: "rgba(239,68,68,0.18)",
    border: "rgba(239,68,68,0.45)",
    text: "#f87171",
    bar: "#ef4444",
  };
}

export default function SectorHeatmap() {
  const [sectors, setSectors] = useState(
    SECTORS.map((s) => ({ ...s, loading: true })),
  );
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadSectors = useCallback(async () => {
    setSectors(SECTORS.map((s) => ({ ...s, loading: true })));

    const results = await Promise.allSettled(
      SECTORS.map(({ ticker }) => fetchSectorData(ticker)),
    );

    setSectors(
      SECTORS.map((meta, i) => ({
        ...meta,
        loading: false,
        ...(results[i].status === "fulfilled" ? results[i].value : {}),
        error: results[i].status === "rejected",
      })),
    );

    setLastUpdated(new Date());
  }, []);

  useEffect(() => {
    const timer = setTimeout(loadSectors, 1500);
    const id = setInterval(loadSectors, 5 * 60 * 1000);
    return () => {
      clearTimeout(timer);
      clearInterval(id);
    };
  }, [loadSectors]);

  return (
    <div className="sh-wrap">
      <div className="sh-header">
        <span className="sh-title">Sector Heatmap</span>
        <div className="sh-legend">
          <span className="sh-leg-item">
            <span className="sh-leg-dot up" /> Gaining
          </span>
          <span className="sh-leg-item">
            <span className="sh-leg-dot down" /> Losing
          </span>
        </div>
        <button className="sh-refresh" onClick={loadSectors} title="Refresh">
          ↻
        </button>
      </div>

      <div className="sh-grid">
        {sectors.map((sector) => {
          const pct = parseFloat(sector.change_percent) || 0;
          const colors = getHeatColors(pct);
          const isUp = pct >= 0;
          // Bar width: scale 0–5% change → 0–100%
          const barWidth = Math.min((Math.abs(pct) / 5) * 100, 100);

          if (sector.loading) {
            return (
              <div key={sector.name} className="sh-cell skeleton">
                <div className="sh-skel-name" />
                <div className="sh-skel-val" />
              </div>
            );
          }

          return (
            <div
              key={sector.name}
              className={`sh-cell ${isUp ? "gaining" : "losing"}`}
              style={{ background: colors.bg, borderColor: colors.border }}
            >
              <span className="sh-cell-name">{sector.name}</span>

              {sector.error || !sector.price ? (
                <span className="sh-cell-pct" style={{ color: "#334155" }}>
                  —
                </span>
              ) : (
                <>
                  <div className="sh-cell-bottom">
                    <span className="sh-cell-pct" style={{ color: colors.text }}>
                      {isUp ? "+" : ""}
                      {pct.toFixed(2)}%
                    </span>
                  </div>
                  <div className="sh-cell-bar">
                    <div
                      className="sh-cell-bar-fill"
                      style={{
                        width: `${barWidth}%`,
                        background: colors.bar,
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {lastUpdated && (
        <div className="sh-footer">
          Last updated{" "}
          {lastUpdated.toLocaleTimeString("en-IN", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      )}
    </div>
  );
}